import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, Progress, Button } from "@learn-me/ui";
import { useAuthStore } from "@/features/auth/store/authStore";
import { api } from "@/lib/api";
import {
  BookOpen,
  PlayCircle,
  Award,
  Clock,
  Trophy,
  CheckCircle,
  HelpCircle,
  Star,
  Heart,
  Download,
  Share2,
} from "lucide-react";
import type { ApiResponse } from "@learn-me/types";
import { motion } from "framer-motion";
import { FadeIn, StaggeredGrid, AnimatedCounter } from "@/components/animated";
import { useReducedMotion } from "@/hooks";
import { GamificationSummaryCard } from "@/features/gamification";
import { useRecentActivity } from "@/features/activity";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { requestCertificate } from "@/features/certificates";
import toast from "react-hot-toast";
import { getErrorMessage } from "@/lib/api";

interface DashboardData {
  enrolledCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  recentCourses: Array<{
    id: string;
    courseId: string;
    progress: number;
    lastAccessedAt?: string;
    course: {
      id: string;
      title: string;
      slug: string;
      thumbnailUrl?: string;
      chapters?: Array<{
        id: string;
        title: string;
        userProgress?: {
          isCompleted: boolean;
        } | null;
      }>;
    };
  }>;
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const prefersReducedMotion = useReducedMotion();
  const [certificateActionCourseId, setCertificateActionCourseId] = useState<
    string | null
  >(null);

  const { data, isLoading } = useQuery({
    queryKey: ["student-dashboard"],
    queryFn: async (): Promise<DashboardData> => {
      // Usamos /enrollments/my-courses (que sí filtra por usuario y trae
      // progreso) y agregamos los contadores en el cliente. El endpoint
      // /courses?enrolled=true no aplica el filtro, por eso antes el
      // dashboard salía vacío.
      const response = await api.get<
        ApiResponse<{
          items: Array<{
            id: string;
            progress: number;
            lastAccessedAt?: string | null;
            course: {
              id: string;
              title: string;
              slug: string;
              thumbnailUrl?: string | null;
              chapters?: Array<{
                id: string;
                title: string;
                userProgress?: { isCompleted: boolean } | null;
              }>;
            };
          }>;
          pagination: { total: number };
        }>
      >("/enrollments/my-courses?limit=50");
      const items = response.data.data?.items ?? [];
      const total = response.data.data?.pagination.total ?? items.length;
      const completedCourses = items.filter((e) => e.progress >= 100).length;
      const inProgressCourses = items.filter(
        (e) => e.progress > 0 && e.progress < 100,
      ).length;
      const recentCourses = items.slice(0, 5).map((e) => ({
        id: e.id,
        courseId: e.course.id,
        progress: e.progress,
        lastAccessedAt: e.lastAccessedAt ?? undefined,
        course: {
          id: e.course.id,
          title: e.course.title,
          slug: e.course.slug,
          thumbnailUrl: e.course.thumbnailUrl ?? undefined,
          chapters: e.course.chapters,
        },
      }));
      return {
        enrolledCourses: total,
        completedCourses,
        inProgressCourses,
        recentCourses,
      };
    },
  });

  const { data: recentActivity } = useRecentActivity(5);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "chapter_completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "quiz_passed":
        return <Award className="h-4 w-4 text-blue-500" />;
      case "quiz_failed":
        return <HelpCircle className="h-4 w-4 text-orange-500" />;
      case "badge_earned":
        return <Star className="h-4 w-4 text-yellow-500" />;
      case "course_completed":
        return <Trophy className="h-4 w-4 text-purple-500" />;
      case "enrollment":
        return <BookOpen className="h-4 w-4 text-primary" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActivityMessage = (activity: {
    type: string;
    metadata?: Record<string, unknown>;
  }) => {
    const title = (activity.metadata?.title as string) || "Sin título";
    switch (activity.type) {
      case "chapter_completed":
        return `Completaste "${title}"`;
      case "quiz_passed":
        return `Aprobaste el quiz "${title}" con ${activity.metadata?.score}%`;
      case "quiz_failed":
        return `No aprobaste el quiz "${title}"`;
      case "badge_earned":
        return `Ganaste el badge "${title}"`;
      case "course_completed":
        return `Completaste el curso "${title}"`;
      case "enrollment":
        return `Te inscribiste en "${title}"`;
      default:
        return title;
    }
  };

  const ensureCompletionTitle = async (courseId: string) => {
    setCertificateActionCourseId(courseId);
    try {
      return await requestCertificate(courseId);
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setCertificateActionCourseId(null);
    }
  };

  const handleDownloadTitle = async (courseId: string) => {
    const certificate = await ensureCompletionTitle(courseId);
    if (!certificate) return;

    window.location.href = `${api.defaults.baseURL ?? "/api"}/certificates/download/${encodeURIComponent(certificate.code)}`;
  };

  const handleShareTitle = async (courseId: string, courseTitle: string) => {
    const certificate = await ensureCompletionTitle(courseId);
    if (!certificate) return;

    const url = `${window.location.origin}/verify?code=${encodeURIComponent(
      certificate.code,
    )}`;
    const text = `Completé "${courseTitle}" en ARCUS Learning. Puedes verificar mi título de completitud aquí: ${url}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Título de completitud",
          text,
          url,
        });
        return;
      } catch {
        return;
      }
    }

    await navigator.clipboard.writeText(url);
    toast.success("Link público copiado");
  };

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <FadeIn direction="up">
        <div>
          <h1 className="text-3xl font-bold">Bienvenido, {user?.firstName}!</h1>
          <p className="text-muted-foreground">
            Continua aprendiendo donde lo dejaste.
          </p>
        </div>
      </FadeIn>

      {/* Stats */}
      <StaggeredGrid
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        staggerDelay={0.1}
      >
        <motion.div
          whileHover={prefersReducedMotion ? {} : { y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="h-full min-h-[120px]">
            <CardContent className="h-full p-6 flex items-center">
              <div className="flex items-center gap-4 w-full">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <BookOpen className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Cursos Inscritos
                  </p>
                  <p className="text-2xl font-bold">
                    {isLoading ? (
                      "-"
                    ) : (
                      <AnimatedCounter value={data?.enrolledCourses || 0} />
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          whileHover={prefersReducedMotion ? {} : { y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="h-full min-h-[120px]">
            <CardContent className="h-full p-6 flex items-center">
              <div className="flex items-center gap-4 w-full">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <Award className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completados</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? (
                      "-"
                    ) : (
                      <AnimatedCounter value={data?.completedCourses || 0} />
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          whileHover={prefersReducedMotion ? {} : { y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="h-full min-h-[120px]">
            <CardContent className="h-full p-6 flex items-center">
              <div className="flex items-center gap-4 w-full">
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <PlayCircle className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En Progreso</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? (
                      "-"
                    ) : (
                      <AnimatedCounter value={data?.inProgressCourses || 0} />
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          whileHover={prefersReducedMotion ? {} : { y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="h-full min-h-[120px]">
            <CardContent className="h-full p-6 flex items-center">
              <div className="flex items-center gap-4 w-full">
                <div className="p-3 bg-orange-500/10 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Horas de Estudio
                  </p>
                  <p className="text-2xl font-bold">-</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </StaggeredGrid>

      {/* Continue Learning - Featured Widget */}
      {!isLoading &&
        data?.recentCourses &&
        data.recentCourses.length > 0 &&
        data.recentCourses.some((e) => e.progress > 0 && e.progress < 100) && (
          <FadeIn direction="up" delay={0.15}>
            {(() => {
              const inProgressCourse = data.recentCourses.find(
                (e) => e.progress > 0 && e.progress < 100,
              );
              if (!inProgressCourse) return null;

              const nextChapter = inProgressCourse.course.chapters?.find(
                (ch) => !ch.userProgress?.isCompleted,
              );

              return (
                <Card className="bg-gradient-to-r from-primary-600 to-primary-700 text-white overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Thumbnail */}
                      <div className="w-full md:w-48 h-32 bg-black/20 rounded-lg overflow-hidden flex-shrink-0">
                        {inProgressCourse.course.thumbnailUrl ? (
                          <img
                            src={
                              normalizeImageUrl(
                                inProgressCourse.course.thumbnailUrl,
                              ) ?? ""
                            }
                            alt={inProgressCourse.course.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-12 w-12 text-white/50" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-primary-100 text-sm mb-1">
                          Continuar aprendiendo
                        </p>
                        <h3 className="text-xl font-bold mb-2 line-clamp-1">
                          {inProgressCourse.course.title}
                        </h3>

                        {nextChapter && (
                          <p className="text-primary-100 text-sm mb-3">
                            Siguiente: {nextChapter.title}
                          </p>
                        )}

                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-primary-100">Progreso</span>
                            <span className="font-medium">
                              {inProgressCourse.progress}%
                            </span>
                          </div>
                          <Progress
                            value={inProgressCourse.progress}
                            className="h-2 bg-white/20"
                          />
                        </div>

                        <Link to={`/learn/${inProgressCourse.course.slug}`}>
                          <Button
                            variant="secondary"
                            size="lg"
                            className="bg-white text-primary-700 hover:bg-primary-50"
                          >
                            <PlayCircle className="mr-2 h-5 w-5" />
                            Continuar
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </FadeIn>
        )}

      {/* Gamification Summary */}
      <FadeIn direction="up" delay={0.2}>
        <GamificationSummaryCard />
      </FadeIn>

      {/* Recent Courses */}
      <FadeIn direction="up" delay={0.3}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Mis Cursos</h2>
              <Link to="/my-courses">
                <Button variant="ghost" size="sm">
                  Ver todos
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="animate-pulse flex gap-4 p-4 border rounded-lg"
                  >
                    <div className="w-32 h-20 bg-muted rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !data?.recentCourses?.length ? (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No tienes cursos aún</h3>
                <p className="text-muted-foreground mb-4">
                  Explora nuestro catálogo y comienza a aprender.
                </p>
                <Link to="/courses">
                  <Button>Ver cursos</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {data.recentCourses.map((enrollment, index) => (
                  <motion.div
                    key={enrollment.id}
                    initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={prefersReducedMotion ? {} : { x: 4 }}
                  >
                    <div className="flex flex-col gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors md:flex-row md:items-center">
                      <div className="w-32 h-20 bg-muted rounded overflow-hidden flex-shrink-0 relative">
                        {enrollment.course.thumbnailUrl ? (
                          <img
                            src={
                              normalizeImageUrl(
                                enrollment.course.thumbnailUrl,
                              ) ?? ""
                            }
                            alt={enrollment.course.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        {enrollment.progress === 100 && (
                          <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center">
                            <Trophy className="h-6 w-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">
                          {enrollment.course.title}
                        </h3>
                        <div className="mt-2">
                          <div className="flex justify-between text-sm text-muted-foreground mb-1">
                            <span>
                              {enrollment.progress === 100
                                ? "Completado"
                                : enrollment.progress === 0
                                  ? "Sin iniciar"
                                  : `${enrollment.progress}% completado`}
                            </span>
                          </div>
                          <Progress
                            value={enrollment.progress}
                            className="h-2"
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 md:justify-end">
                        {enrollment.progress >= 100 ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDownloadTitle(enrollment.course.id)
                              }
                              disabled={
                                certificateActionCourseId ===
                                enrollment.course.id
                              }
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Descargar título
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleShareTitle(
                                  enrollment.course.id,
                                  enrollment.course.title,
                                )
                              }
                              disabled={
                                certificateActionCourseId ===
                                enrollment.course.id
                              }
                            >
                              <Share2 className="mr-2 h-4 w-4" />
                              Compartir
                            </Button>
                          </>
                        ) : (
                          <Link to={`/learn/${enrollment.course.slug}`}>
                            <Button variant="ghost" size="sm">
                              <PlayCircle className="h-5 w-5" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </FadeIn>

      {/* Recent Activity */}
      {recentActivity && recentActivity.length > 0 && (
        <FadeIn direction="up" delay={0.4}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Actividad Reciente</h2>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={prefersReducedMotion ? {} : { opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex-shrink-0 p-2 bg-background rounded-full">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {getActivityMessage(activity)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(activity.createdAt), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      )}

      {/* Wishlist shortcut */}
      <FadeIn direction="up" delay={0.5}>
        <Link to="/wishlist">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Heart className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-medium">Mis Favoritos</p>
                    <p className="text-sm text-muted-foreground">
                      Cursos guardados para después
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  Ver todos
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>
      </FadeIn>
    </div>
  );
}
